<template>
  <v-card class="ma-0 pa-2 card" color="background" density="compact" variant="elevated" elevation="4">
    <v-form ref="formRef" density="compact" @submit.prevent>
      <v-card-item v-if="!show" class="schedule-summary" @click="show = true">
        <template #prepend>
          <v-avatar size="40" rounded="lg" class="mr-3 schedule-summary-avatar">
            <v-icon icon="mdi-calendar-clock-outline" size="small"></v-icon>
          </v-avatar>
        </template>
        <v-card-title class="text-truncate">{{ label || agentName || 'New Schedule' }}</v-card-title>
        <v-card-subtitle class="text-truncate">{{ scheduleSubtitle }}</v-card-subtitle>
        <template #append>
          <v-btn color="primary" icon="mdi-chevron-down" size="small" variant="plain" @click.stop="show = true" />
        </template>
      </v-card-item>
      <v-card-text v-if="!show" class="pt-0 pb-2 schedule-summary-text">
        <div class="d-flex flex-wrap align-center">
          <v-chip class="mr-2 mb-2" :color="status === 'enabled' ? 'success' : 'grey'" size="small">{{ status }}</v-chip>
          <v-chip v-if="nextRunLabel" class="mr-2 mb-2" size="small" variant="tonal">Next {{ nextRunLabel }}</v-chip>
          <v-chip v-if="lastRunLabel" class="mr-2 mb-2" size="small" variant="outlined">Last {{ lastRunLabel }}</v-chip>
        </div>
      </v-card-text>
      <v-expand-transition>
        <v-list v-show="show" class="bg-background">
          <v-list-item>
            <v-select v-model="agentName" :items="agentNames" label="Agent to Run" variant="outlined" density="compact"
              :rules="[v => !!v || 'Agent is required']" class="pt-2"></v-select>
          </v-list-item>
          <v-list-item>
            <v-text-field v-model="label" label="Label" variant="outlined" density="compact"
              placeholder="e.g. Daily news summary" hint="A descriptive name for this schedule"
              persistent-hint class="pt-2" maxlength="120" counter
              :rules="labelRules"></v-text-field>
          </v-list-item>
          <v-list-item>
            <v-textarea v-model="prompt" label="Prompt" variant="outlined" density="compact"
              :rules="[v => !!v || 'Prompt is required']" class="pt-2"></v-textarea>
          </v-list-item>
          <v-list-item>
            <v-text-field :model-value="formattedStartDate" label="Start Date" variant="outlined" density="compact"
              readonly @click="showStartDateDialog = true" class="pt-2"></v-text-field>
            <v-dialog v-model="showStartDateDialog" max-width="300px">
              <v-date-picker v-model="startDate" @update:model-value="showStartDateDialog = false"></v-date-picker>
            </v-dialog>
          </v-list-item>
          <v-list-item>
            <v-select v-model="repeatInterval" :items="['daily', 'weekly', 'monthly', 'once']" label="Frequency"
              variant="outlined" density="compact" class="pt-2"></v-select>
          </v-list-item>

          <v-list-item-title class="pl-4 text-caption">Times to Run ({{ userTimezone }})</v-list-item-title>
          <v-list-item v-for="(time, index) in localTimesToRun" :key="index">
            <v-row align="center" class="py-1">
              <v-col cols="4" class="py-0">
                <v-number-input v-model="time.hour" label="Hour" :min="1" :max="12" control-variant="stacked"
                  variant="outlined" density="compact" hide-details class="pt-3 pb-2"></v-number-input>
              </v-col>
              <v-col cols="4" class="py-0">
                <v-number-input v-model="time.minute" label="Minute" :min="0" :max="59" control-variant="stacked"
                  variant="outlined" density="compact" hide-details class="pt-3 pb-2"></v-number-input>
              </v-col>
              <v-col cols="3" class="py-0">
                <v-select v-model="time.ampm" :items="['AM', 'PM']" density="compact" hide-details variant="outlined"
                  class="pt-3 pb-2"></v-select>
              </v-col>
              <v-col cols="1" class="py-0">
                <v-btn v-if="localTimesToRun.length > 1" icon="mdi-minus-circle" variant="text"
                  @click="removeTime(index)"></v-btn>
              </v-col>
            </v-row>
          </v-list-item>
          <v-list-item>
            <v-btn @click="addTime" size="small" prepend-icon="mdi-plus">Add Time</v-btn>
          </v-list-item>

          <v-list-item v-if="repeatInterval === 'weekly'">
            <v-select v-model="daysOfWeek" :items="weekdays" item-title="title" item-value="value" label="Days of Week"
              multiple chips variant="outlined" density="compact"></v-select>
          </v-list-item>
          <v-list-item v-if="repeatInterval === 'monthly'">
            <v-number-input v-model="dayOfMonth" label="Day of Month" :min="1" :max="31"
              control-variant="stacked"></v-number-input>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="sendEmail" :label="`Send result to: ${email}`" hide-details></v-checkbox>
          </v-list-item>
        </v-list>
      </v-expand-transition>

      <v-card-actions>
        <Button v-if="show" color="primary" :click="saveSchedule" icon="mdi-check-bold" tooltip="Save" size="large" />
        <Button v-if="show && !isNew" color="primary" :click="initiateDelete" icon="mdi-trash-can-outline"
          tooltip="Delete" size="large" />
        <v-switch v-if="show && !isNew" v-model="enabled" @change="toggleStatus" :label="status" color="primary"
          hide-details inset></v-switch>
        <Button v-if="show && !isNew" color="primary" :click="openHistoryDialog" icon="mdi-history"
          tooltip="View History" size="large" />
        <v-spacer />
        <v-btn :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="show = !show"></v-btn>
      </v-card-actions>
    </v-form>
    <ConfirmationDialog v-model="showDeleteDialog" title="Confirm Deletion"
      message="Are you sure you want to delete this schedule?" @confirm="confirmDelete" />
    <ScheduledRunHistoryDialog :run-id="runId" :show="showHistoryDialog" v-model="showHistoryDialog" />
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapGetters } from 'vuex';
import { ScheduledRun, NewScheduledRunPayload, UpdateScheduledRunPayload } from '@/types/schedules';
import Button from '@/components/sub/Button.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import ScheduledRunHistoryDialog from '@/components/dialogs/ScheduledRunHistoryDialog.vue';
import {
  formatLocalDateTime,
  getTimeZoneAbbreviation,
  type Local12HourTime,
  local12HourTimesToUtc,
  utcTimesToLocal12Hour
} from '@/utils/scheduleTime'
import { buildScheduledRunPayload } from '@/utils/schedules'
// Note: v-number-input is assumed to be a registered component or needs to be imported.
// v-date-picker is a standard Vuetify component.

@Options({
  props: { id: String },
  components: { Button, ConfirmationDialog, ScheduledRunHistoryDialog },
  computed: {
    ...mapGetters(['agentNames', 'getScheduledRunById', 'email']),
  },
  emits: ['remove-card'],
})
export default class ScheduledRunCard extends Vue {
  id!: string;
  agentNames!: string[];
  getScheduledRunById!: (id: string) => ScheduledRun | undefined;
  email!: string;

  // Form Data
  runId = '';
  agentName = '';
  label = '';
  prompt = '';
  startDate: Date = new Date();
  timesToRun: { hour: number; minute: number }[] = [{ hour: 9, minute: 0 }]; // This will store UTC times
  localTimesToRun: Local12HourTime[] = [{ hour: 9, minute: 0, ampm: 'AM' }]; // This is for the UI
  repeatInterval: 'daily' | 'weekly' | 'monthly' | 'once' = 'daily';
  daysOfWeek: number[] = [];
  dayOfMonth = 1;
  status: 'enabled' | 'disabled' | 'running' = 'enabled';
  sendEmail = true;
  userTimezone = '';
  nextRunAtValue: number | null = null;
  lastRunAtValue: number | null = null;
  
  // Component State
  isNew = true;
  show = false;
  showDeleteDialog = false;
  showHistoryDialog = false;
  showStartDateDialog = false;
  weekdays = [
    { title: 'Sunday', value: 0 }, { title: 'Monday', value: 1 }, { title: 'Tuesday', value: 2 },
    { title: 'Wednesday', value: 3 }, { title: 'Thursday', value: 4 }, { title: 'Friday', value: 5 },
    { title: 'Saturday', value: 6 },
  ];
  formRef: { validate: () => Promise<{ valid: boolean }> } | null = null;

  get enabled() { return this.status === 'enabled'; }
  set enabled(val: boolean) { this.status = val ? 'enabled' : 'disabled'; }
  get formattedStartDate() { return this.startDate.toLocaleDateString(); }
  get nextRunLabel() { return this.nextRunAtValue ? formatLocalDateTime(this.nextRunAtValue) : ''; }
  get lastRunLabel() { return this.lastRunAtValue ? formatLocalDateTime(this.lastRunAtValue) : ''; }
  get scheduleSubtitle() { return `${this.agentName || 'No agent'} · ${this.repeatInterval}`; }
  get labelRules() {
    return [
      (value: string | undefined) => !!value?.trim() || 'Label is required',
      (value: string | undefined) => (value?.trim().length || 0) <= 120 || 'Label must be 120 characters or less',
    ];
  }

  mounted() {
    this.userTimezone = getTimeZoneAbbreviation(new Date());
    if (!this.id) {
      this.isNew = true;
      this.show = true;
    } else {
      this.isNew = false;
      this.updateData();
    }
  }

  updateData() {
    const run = this.getScheduledRunById(this.id);
    if (run) {
      this.runId = run.id;
      this.agentName = run.agentName;
      this.label = run.label || '';
      this.prompt = run.prompt;
      this.startDate = new Date(run.startDate);
      this.timesToRun = run.timesToRun;
      this.localTimesToRun = utcTimesToLocal12Hour(run.timesToRun, this.startDate);
      this.repeatInterval = run.repeatInterval;
      this.daysOfWeek = run.daysOfWeek || [];
      this.dayOfMonth = run.dayOfMonth || 1;
      this.status = run.status;
      this.sendEmail = run.sendEmail === undefined ? true : run.sendEmail;
      this.nextRunAtValue = run.nextRunAt;
      this.lastRunAtValue = run.lastRunAt ?? null;
    }
  }

  addTime() { this.localTimesToRun.push({ hour: 12, minute: 0, ampm: 'PM' }); }
  removeTime(index: number) { this.localTimesToRun.splice(index, 1); }

  async saveSchedule() {
    const form = this.$refs.formRef as { validate: () => Promise<{ valid: boolean }> } | undefined;
    const validation = form ? await form.validate() : { valid: true };
    const sanitizedLabel = this.label.trim();
    if (!validation.valid) {
      if (!sanitizedLabel) {
        this.$store.dispatch('updateSnackbar', { text: 'Label is required.', color: 'error' }, { root: true });
      }
      return;
    }
    if (!sanitizedLabel) {
      this.$store.dispatch('updateSnackbar', { text: 'Label is required.', color: 'error' }, { root: true });
      return;
    }

    this.label = sanitizedLabel;
    this.timesToRun = local12HourTimesToUtc(this.localTimesToRun, this.startDate);
    // Shared with the simple-mode Automations lens — see src/utils/schedules.ts.
    const payload: NewScheduledRunPayload | UpdateScheduledRunPayload = buildScheduledRunPayload({
      agentName: this.agentName,
      label: sanitizedLabel,
      prompt: this.prompt,
      startDate: this.startDate.getTime(),
      timesToRun: this.timesToRun,
      repeatInterval: this.repeatInterval,
      daysOfWeek: this.daysOfWeek,
      dayOfMonth: this.dayOfMonth,
      enabled: this.enabled,
      sendEmail: this.sendEmail,
    });

    if (this.isNew) {
      const newRun = await this.$store.dispatch('createScheduledRun', payload as NewScheduledRunPayload);
      if (newRun) {
        this.$emit('remove-card');
      }
    } else {
      await this.$store.dispatch('updateScheduledRun', { id: this.runId, payload });
      this.show = false;
    }
  }
  
  initiateDelete() { this.showDeleteDialog = true; }
  async confirmDelete() {
    await this.$store.dispatch('deleteScheduledRun', this.runId);
    this.showDeleteDialog = false;
  }
  
  toggleStatus() {
    this.$store.dispatch('toggleScheduledRun', this.runId);
  }

  openHistoryDialog() { this.showHistoryDialog = true; }
}
</script>

<style scoped lang="scss">
.schedule-summary {
  cursor: pointer;
}

.schedule-summary-avatar {
  background-color: #1f1d16;
  color: #fff;
}

.schedule-summary-text {
  padding-left: 72px;
}
</style>
